import { isLocal } from "~/utils/environment"

const MOCK_AUTH_KEY = "hakio.mockAuth"
const MOCK_USER_KEY = "hakio.mockUser"
// Must match `testUserSession` in server/api/trpc.ts.
const DEFAULT_MOCK_USER_ID = "auth0|123456789"

// A browser opts in through `/login/<org>?mock` and then runs as the e2e mock staff user: no Auth0,
// so no consent page, no session to expire, and a company switch is one URL. Only on a `vite dev`
// server on localhost with VITE_MOCK_AUTH=1 in its .env; createTRPCContext enforces the server half.
const canMockAuth = () => import.meta.env.DEV && import.meta.env.VITE_MOCK_AUTH === "1" && isLocal()

export const isMockAuth = () =>
  import.meta.env.VITE_IS_TESTING === "true" ||
  import.meta.env.VITE_IS_E2E_TESTING === "true" ||
  (canMockAuth() && localStorage.getItem(MOCK_AUTH_KEY) === "1")

/** `/login/<org>?mock&user=<name>` signs a browser in as its own user, so two browsers are two
 * users: each other's saves arrive as foreign updates instead of being skipped as their own. */
export const mockUserId = () => {
  const name = localStorage.getItem(MOCK_USER_KEY)
  return name ? `auth0|mock-${name}` : DEFAULT_MOCK_USER_ID
}

/** Company switch under mock auth: Auth0's `loginWithRedirect` is a no-op in TestAuth0Provider. */
export function mockSwitchOrganization(orgName: string) {
  const user = localStorage.getItem(MOCK_USER_KEY)
  const query = user ? `?mock&user=${encodeURIComponent(user)}` : "?mock"
  window.location.assign(`/login/${encodeURIComponent(orgName)}${query}`)
}

/** Identity headers the server reads in place of a token in test mode. */
export function mockAuthHeaders(): Record<string, string> {
  const testOrgId = localStorage.getItem("selectedOrgId")
  return testOrgId ? { "x-test-org-id": testOrgId, "x-test-user-id": mockUserId() } : {}
}

/** Applies `/login/<org>[?mock[&user=<name>]]` and returns whether the page has to reload to swap
 * auth providers. */
export function applyMockAuthLogin(orgName: string, mock: boolean, user: string | null): boolean {
  if (!canMockAuth()) return false
  if (mock) {
    localStorage.setItem(MOCK_AUTH_KEY, "1")
    localStorage.setItem("selectedOrgId", orgName)
    if (user) localStorage.setItem(MOCK_USER_KEY, user)
    else localStorage.removeItem(MOCK_USER_KEY)
    return true
  }
  if (localStorage.getItem(MOCK_AUTH_KEY) !== "1") return false
  localStorage.removeItem(MOCK_AUTH_KEY)
  localStorage.removeItem(MOCK_USER_KEY)
  return true
}
